import React, { useEffect, useState } from "react";
import styled from "styled-components";
import sanityClient from "../sanityClient";
import tempImage from "../assets/images/temp.jpg"; // Ensure the path is correct
import { Link } from "react-router-dom";

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Fetch regular events
        const regularEvents = await sanityClient.fetch(
          `*[_type == "event" && endDate > now()] | order(startDate asc){
            _id,
            title,
            description,
            startDate,
            endDate,
            location,
            address,
            "imageUrl": image.asset->url,
            actionButtonText,
            actionButtonLink
          }`
        );

        // Fetch fallback events
        const fallbackEvents = await sanityClient.fetch(
          `*[_type == "fallbackEvent"] | order(_createdAt desc)[0..2]{
            _id,
            title,
            description,
            "imageUrl": image.asset->url,
            actionButtonText,
            actionButtonLink
          }`
        );

        // Format and combine the data
        const formattedRegularEvents = regularEvents.map((item) =>
          formatRegularEvent(item)
        );
        const formattedFallbackEvents = fallbackEvents.map((item) =>
          formatFallbackEvent(item)
        );

        const combinedEvents = [
          ...formattedRegularEvents,
          ...formattedFallbackEvents,
        ];

        // Ensure at least 3 events
        while (combinedEvents.length < 3) {
          combinedEvents.push(createPlaceholderEvent());
        }

        const limitedEvents = combinedEvents.slice(0, 3);
        setEvents(limitedEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    fetchEvents();
  }, []);

  const formatRegularEvent = (item) => ({
    id: item._id,
    title: item.title,
    description: formatExcerpt(item.description),
    startDate: new Date(item.startDate),
    endDate: new Date(item.endDate),
    location: item.location,
    address: item.address,
    image: item.imageUrl || tempImage,
    actionButtonText: item.actionButtonText || "Learn More",
    actionButtonLink:
      item.actionButtonLink || `/get-involved/events/${item._id}`,
    isFallback: false,
  });

  const formatFallbackEvent = (item) => ({
    id: item._id,
    title: item.title,
    description: item.description, // Full description for fallback events
    image: item.imageUrl || tempImage,
    actionButtonText: item.actionButtonText,
    actionButtonLink: item.actionButtonLink,
    isFallback: true,
  });

  const createPlaceholderEvent = () => ({
    id: `placeholder-${Math.random()}`,
    title: "Build Site Volunteer Sign Up",
    description: "Join us to help build homes for those in need...",
    image: tempImage,
    actionButtonText: "Sign Up",
    actionButtonLink: "/get-involved/volunteer",
    isFallback: true,
  });

  const formatExcerpt = (text) => {
    return text.length > 100 ? text.slice(0, 100) + "..." : text;
  };

  return (
    <SectionContainer>
      <Container>
        <Title>Upcoming Events and Activities</Title>
        <EventGrid>
          {events.map((event, index) => (
            <EventCard key={index}>
              <EventImage src={event.image} alt={event.title} />
              <EventContent>
                <EventTitle>{event.title}</EventTitle>
                {!event.isFallback && (
                  <>
                    <EventDate>
                      <strong>Date:</strong>{" "}
                      {event.startDate.toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </EventDate>
                    <EventLocation>
                      <strong>Location:</strong> {event.location}
                    </EventLocation>
                  </>
                )}
                <EventDescription full={event.isFallback}>
                  {event.description}
                </EventDescription>
                <ButtonContainer>
                  {!event.isFallback && (
                    <LearnMoreButton
                      as={Link}
                      to={`/get-involved/events/${event.id}`}
                    >
                      Learn More
                    </LearnMoreButton>
                  )}
                  <ActionButton href={event.actionButtonLink} target="_blank">
                    {event.actionButtonText}
                  </ActionButton>
                </ButtonContainer>
              </EventContent>
            </EventCard>
          ))}
        </EventGrid>
        <AllEventsLink to="/get-involved/events">See All Events</AllEventsLink>
      </Container>
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  background-color: var(--hhb-green); /* Use global primary color */
  padding: 40px 0;
`;

const Container = styled.div`
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
  color: var(--hhb-white);
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2rem;
  color: var(--hhb-white);
  margin-bottom: 20px;
`;

const EventGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: space-around;
`;

const EventCard = styled.div`
  background: var(--hhb-white);
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  flex: 1 1 calc(33% - 20px); /* Responsive grid */
  text-decoration: none; /* Ensure the link does not have an underline */
  color: inherit; /* Inherit color to maintain styling */
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex: 1 1 100%; /* Full width on smaller screens */
  }
`;

const EventImage = styled.img`
  width: 100%;
  height: 200px; /* Adjust image size for better visibility */
  object-fit: cover;
`;

const EventContent = styled.div`
  padding: 20px; /* Increased padding for better spacing */
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
`;

const EventTitle = styled.h3`
  display: block;
  font-size: 1.5rem; /* Increased font size */
  font-weight: 700;
  color: var(--hhb-darkblue); /* Changed font color to dark blue */
  margin-bottom: 10px; /* Increased margin */
  transition: color 0.3s;

  &:hover {
    color: var(--hhb-orange); /* Change color on hover for better feedback */
  }
`;

const EventDate = styled.p`
  font-size: 1rem; /* Adjusted font size */
  color: #333; /* Changed font color to dark gray */
  margin-bottom: 10px; /* Adjusted margin */
`;

const EventLocation = styled.p`
  font-size: 1rem; /* Adjusted font size */
  color: #333; /* Changed font color to dark gray */
  margin-bottom: 10px; /* Adjusted margin */
`;

const EventDescription = styled.p`
  font-size: 1rem; /* Adjusted font size */
  color: #555; /* Changed font color to medium gray */
  margin-bottom: 20px; /* Increased margin for better spacing */
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.full ? "none" : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const LearnMoreButton = styled(Link)`
  color: var(--hhb-white); /* White text color */
  padding: 8px 12px;
  border-radius: 5px;
  text-decoration: none;
  font-weight: bold;
  text-align: center;
  flex: 1; /* Ensure all buttons are of equal width */
  background-color: var(--hhb-primary); /* Habitat blue */

  &:hover {
    background-color: var(--hhb-darkblue); /* Darker blue on hover */
  }
`;

const ActionButton = styled.a`
  color: var(--hhb-white); /* White text color */
  padding: 8px 12px;
  border-radius: 5px;
  text-decoration: none;
  font-weight: bold;
  text-align: center;
  flex: 1; /* Ensure all buttons are of equal width */
  background-color: var(--hhb-primary); /* Habitat blue */

  &:hover {
    background-color: var(--hhb-darkblue); /* Darker blue on hover */
  }
`;

const AllEventsLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px; /* Increased margin */
  font-size: 1.2rem; /* Increased font size */
  color: var(--hhb-white); /* Changed color to white */
  text-decoration: underline;

  &:hover {
    color: var(--hhb-yellow);
    text-decoration: none;
  }
`;

export default UpcomingEvents;
